import React, { useState } from 'react'
import classnames from 'classnames'
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'

export interface CopyableValueProps {
  value: string
  /** Used in title / aria-label, e.g. "Tenant account number". */
  label: string
  /** Display content; defaults to `value`. */
  children?: React.ReactNode
  className?: string
  /** Extra classes for the value text. */
  valueClassName?: string
  iconClassName?: string
  /** Use monospace for IDs/numbers. Default true. */
  mono?: boolean
  /** Truncate overflowing text with ellipsis. Default true. */
  truncate?: boolean
}

/**
 * Clickable value + copy icon. Hover highlights the whole control;
 * a brief green flash confirms a successful copy.
 */
export const CopyableValue: React.FC<CopyableValueProps> = ({
  value,
  label,
  children,
  className,
  valueClassName,
  iconClassName = 'h-3.5 w-3.5',
  mono = true,
  truncate = true,
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = (event: React.MouseEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied' : `Copy ${label}`}
      aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
      className={classnames(
        'inline-flex items-center gap-1 rounded -mx-1 px-1 py-0.5 text-left transition-colors',
        truncate ? 'max-w-full min-w-0' : 'whitespace-nowrap',
        copied
          ? 'bg-emerald-100 dark:bg-emerald-900/50'
          : 'hover:bg-slate-200/80 dark:hover:bg-slate-600/70',
        className
      )}
    >
      <span
        className={classnames(
          mono && 'font-mono tabular-nums',
          truncate && 'min-w-0 truncate',
          valueClassName
        )}
        title={value}
      >
        {children ?? value}
      </span>
      <span
        className={classnames(
          'inline-flex shrink-0',
          copied ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
        )}
        aria-hidden="true"
      >
        {copied ? (
          <CheckIcon className={iconClassName} />
        ) : (
          <ClipboardDocumentIcon className={iconClassName} />
        )}
      </span>
    </button>
  )
}

export default CopyableValue
